import * as React from "react";

// useLayoutEffect reads matchMedia before the first browser paint so the
// initial `false` state does not flash the wrong breakpoint; useEffect on the
// server avoids the useLayoutEffect SSR warning.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export function useMediaQuery(query: string) {
  // Lazy-init from matchMedia so the FIRST render already has the correct
  // breakpoint. Initializing to a bare `false` made every responsive branch
  // (e.g. drawer-dialog's isDesktop ? Dialog : Drawer) mount its mobile tree
  // first and then remount as desktop — losing focus and input state.
  const [value, setValue] = React.useState(
    () => typeof window !== "undefined" && matchMedia(query).matches,
  );

  useIsomorphicLayoutEffect(() => {
    function onChange(event: MediaQueryListEvent) {
      setValue(event.matches);
    }

    const result = matchMedia(query);
    result?.addEventListener?.("change", onChange);
    setValue(result?.matches ?? false);

    return () => result?.removeEventListener?.("change", onChange);
  }, [query]);

  return value;
}
